import { memo, useMemo } from "react";
import type {
  ComparisonUnavailableReason,
  ObservationInfo,
  PreprocessingKey,
  SpectrumHitsData,
} from "@/types/spectrumComparison";
import { formatRt } from "@/utils/formatRt";
import { panelCls } from "@/utils/panelCls";

/**
 * 히트 목록 — 참조 라이브러리 전체 순위 (S7 · 2026-08-19).
 *
 * 기획 v2: `docs/2026/260819_표준물질DB화면_기획_v2.md` §4-5 · §5.3.
 *
 * 원본(NIST MS Search)의 좌측 Hit List 에 해당한다. 실험 스펙트럼 하나를 놓고
 * 참조 125종을 유사도 순으로 세운다.
 *
 * **왜 1위만으로는 부족한가.** 원본 화면에서 1·2위가 둘 다 `Prob. 95.3%` 인
 * 장면은 *"이 둘은 구별되지 않는다"* 는 정보다. 1위만 보여주면 그 사실이
 * 사라진다. 그래서 이 목록은
 *
 * * **임계값으로 자르지 않고** (어디서 끊을지는 과제 내 협의 사항),
 * * **동점 구간을 표시하며**,
 * * **AI 후보를 배지로만 표시하고 순위를 바꾸지 않는다** — 1위가 아닐
 *   수 있고 그 사실도 감정인에게 정보다.
 *
 * AI 후보가 참조 DB 에 아예 없어도 순위는 보여준다. 프로덕션 검출의
 * 절반이 그 경우인데, 지금까지는 화면이 아무것도 못 보여줬다.
 *
 * **T1 관찰(스캔) 묶음.** per-scan 동정은 한 스캔에서 후보 여러 개를 낸다.
 * 이 순위는 *후보* 가 아니라 *스캔(질의 스펙트럼)* 에 대한 것이므로, 같은
 * 스캔의 어느 후보를 눌러도 순위가 같다 — 그것이 정상 동작임을 화면이
 * 스스로 말해야 한다(기획 v4 §3-①). `AI 후보` 배지도 그 스캔의 후보 전부에 붙는다.
 */

const UNAVAILABLE_MESSAGE: Record<ComparisonUnavailableReason, string> = {
  no_reference_compound: "이 화합물은 참조 DB에 등록돼 있지 않습니다.",
  no_reference_spectrum: "참조물질로는 등록돼 있으나 실측 스펙트럼이 없습니다.",
  no_experiment_spectrum:
    "이 시료에는 질량 스펙트럼(MS) 스캔이 없습니다. 순위를 낼 실험 자료가 없습니다.",
  // 2026-08-31: 종전에는 이 경우가 no_experiment_spectrum 으로 뭉개져
  // "스캔이 없습니다"(허위 — TIC 는 그 스캔을 그리고 있다)로 표시됐다.
  background_only_spectrum:
    "이 스캔은 배경가스 피크만 있어 '배경가스 제외' 조건으로는 순위를 낼 수 " +
    "없습니다. 위의 '원자료'로 전환하면 배경가스 포함 순위를 볼 수 있습니다.",
  comparison_rejected: "스펙트럼 강도를 읽을 수 없어 순위를 내지 않았습니다.",
};

/** 소수 3자리에서 같은 점수를 동점으로 본다 — 화면에 그렇게 표시되므로. */
const TIE_DIGITS = 3;

const PREPROCESSING_LABEL: Record<PreprocessingKey, string> = {
  none: "원자료",
  background_excluded: "배경가스 제외",
};

interface SpectrumHitListProps {
  hits: SpectrumHitsData | null | undefined;
  isLoading?: boolean;
  selectedCompoundId?: number | null;
  onSelect?: (compoundId: number) => void;
  onPreprocessingChange?: (preprocessing: PreprocessingKey) => void;
}

const LIBRARY_STATUS_LABEL: Record<string, string> = {
  no_reference_compound: "참조 DB 미등재",
  no_reference_spectrum: "참조 스펙트럼 없음",
};

function libraryStatusLabel(
  c: ObservationInfo["candidates"][number],
): string | null {
  const status =
    c.library_status ?? (c.in_library ? "evaluated" : "no_reference_compound");
  return LIBRARY_STATUS_LABEL[status] ?? null;
}

/** T1 — 기준 스캔과 이 관찰에 기록된 후보 전부. 후보가 둘 이상이면 순위표 공유를 명시. */
function ObservationNote({ observation }: { observation: ObservationInfo }) {
  const many = observation.candidate_count > 1;
  return (
    <div
      data-testid="observation-note"
      className="mb-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700"
    >
      <span className="font-medium">
        기준 RT{" "}
        {observation.scan_rt != null
          ? `${formatRt(observation.scan_rt)} min`
          : "미상"}
      </span>
      {" · "}이 관찰에 기록된 AI 후보 {observation.candidate_count}개
      {many && (
        <>
          {": "}
          {observation.candidates.map((c, i) => {
            const label = libraryStatusLabel(c);
            return (
              <span key={c.result_id}>
                {i > 0 && " · "}
                {c.compound_name}
                {label && <span className="text-slate-400"> ({label})</span>}
              </span>
            );
          })}
          <br />
          후보끼리는 같은 스펙트럼에 대한 대안이라{" "}
          <strong>어느 후보를 눌러도 같은 순위표가 나옵니다.</strong> 독립
          검출로 세지 마십시오.{" "}
          <span
            className="text-slate-500 cursor-help"
            title="이 스캔에 기록된 AI 후보 — 모델이 이 스캔에 대해 낸 후보 중 내부 기준 이상 전부입니다 (2026-08-31 부터 같은 물질도 스캔마다 기록)."
          >
            (기록 규칙 ⓘ)
          </span>
        </>
      )}
    </div>
  );
}

/**
 * 2026-08-28: 이름을 「참조 스펙트럼 순위」로 바꿨다. 옛 이름 "후보 목록"은 같은
 * 화면의 AI 동정 후보표("후보 물질 목록")와 한 단어 차이라, 비교 패널의 빈 상태
 * 문구("후보 목록에서 물질을 선택하면…")가 어느 표를 가리키는지 알 수 없었다.
 * 이 패널은 후보를 고르는 곳이 아니라, 고른 후보의 스캔을 참조 스펙트럼과 대조해
 * 낸 순위다. 근거: docs/2026/260827_대시보드_두섹션_필요성_분석.md P1.
 *
 * 제목 단계도 h4 → h3 으로 올렸다. 옆 칸의 스펙트럼 비교(h3)와 같은 층의 패널인데
 * h4 라 상위 섹션의 하위 항목으로 읽혔다.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    // 2026-08-28: 이 패널만 자기 카드를 그리지 않아, 페이지가 대신 `p-4` 카드로
    // 감싸고 있었다. 그 결과 같은 행의 스펙트럼 비교(panelCls)와 안쪽 여백이
    // 달랐다. 페이지의 이중 패딩을 걷어내면서 여백 소유권을 여기로 옮긴다.
    <div className={panelCls("standard", "flex h-full flex-col")}>
      <h3 className="mb-2 flex flex-wrap items-center text-sm font-semibold text-gray-900">
        참조 스펙트럼 순위
        <span className="ml-2 text-xs font-normal text-gray-500">
          (코사인 유사도)
        </span>
      </h3>
      {children}
    </div>
  );
}

/** 전처리 토글 — 순위는 전처리에 따라 완전히 뒤집히므로 어떤 상태에서도 보인다. */
function PreprocessingButtons({
  current,
  onChange,
}: {
  current: PreprocessingKey;
  onChange?: (preprocessing: PreprocessingKey) => void;
}) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5">
      {(Object.keys(PREPROCESSING_LABEL) as PreprocessingKey[]).map((key) => (
        <button
          type="button"
          key={key}
          onClick={() => onChange?.(key)}
          disabled={!onChange}
          className={`rounded border px-2 py-0.5 text-[11px] ${
            current === key
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:hover:bg-white"
          }`}
        >
          {PREPROCESSING_LABEL[key]}
        </button>
      ))}
    </div>
  );
}

/** 스캔 앵커(2026-08-31) — 저장된 후보 행 없이 스캔 자체 기준임을 명시. */
function ScanAnchorNote({ retentionTime }: { retentionTime: number | null }) {
  return (
    <div
      data-testid="scan-anchor-note"
      className="mb-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700"
    >
      <span className="font-medium">
        선택한 스캔
        {retentionTime != null && ` (RT ${formatRt(retentionTime)} min)`} 기준
      </span>{" "}
      — 저장된 AI 후보와 무관하게 이 스캔의 스펙트럼을 참조 전체와 비교한
      순위입니다. 「AI 동정 후보」 표에서 물질을 선택하면 그 후보의 스캔
      기준으로 바뀝니다.
    </div>
  );
}

const SpectrumHitList = ({
  hits,
  isLoading = false,
  selectedCompoundId,
  onSelect,
  onPreprocessingChange,
}: SpectrumHitListProps) => {
  /** 같은 점수가 둘 이상인 구간 — "구별되지 않는다"를 화면에 드러낸다. */
  const tiedScores = useMemo(() => {
    const counts = new Map<string, number>();
    for (const hit of hits?.hits ?? []) {
      const key = hit.score.toFixed(TIE_DIGITS);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return new Set(
      [...counts.entries()].filter(([, n]) => n > 1).map(([key]) => key),
    );
  }, [hits]);

  if (isLoading) {
    return (
      <Frame>
        <p className="text-xs text-gray-500">불러오는 중…</p>
      </Frame>
    );
  }

  if (!hits) {
    return (
      <Frame>
        <p className="text-xs text-gray-500">
          「AI 동정 후보」 표에서 물질을 선택하면 참조물질 순위를 냅니다.
        </p>
      </Frame>
    );
  }

  if (hits.unavailable_reason || hits.hits.length === 0) {
    // 2026-08-31: 순위 불가 상태에서도 스캔 앵커 표시와 전처리 토글은 남긴다 —
    // '배경가스뿐' 사유는 원자료로 전환하면 볼 수 있는데, 토글이 함께 사라지면
    // 탈출 수단이 없다 (교차 검토 확정 #9).
    return (
      <Frame>
        {hits.result_id == null && (
          <ScanAnchorNote retentionTime={hits.retention_time} />
        )}
        <PreprocessingButtons
          current={hits.preprocessing}
          onChange={onPreprocessingChange}
        />
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-3">
          <p className="text-xs text-amber-800">
            {hits.unavailable_reason
              ? UNAVAILABLE_MESSAGE[hits.unavailable_reason]
              : "조건에 맞는 참조물질이 없습니다."}
          </p>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <p className="mb-2 text-xs text-gray-600">
        {hits.truncated
          ? `${hits.total_hits}건 중 ${hits.hits.length}건`
          : `${hits.total_hits}건`}{" "}
        · 참조 {hits.reference_library_size}종 전체와 비교
      </p>

      {/* T1: 이 순위는 후보가 아니라 스캔에 대한 것 — 같은 스캔 후보는 순위가 같다 */}
      {hits.observation && <ObservationNote observation={hits.observation} />}

      {/* 스캔 앵커 (2026-08-31) — 무엇 기준인지 화면이 스스로 말해야 한다 */}
      {hits.result_id == null && (
        <ScanAnchorNote retentionTime={hits.retention_time} />
      )}

      {/* 어떤 조건으로 순위를 냈는지 — 순위는 전처리에 따라 완전히 뒤집힌다 */}
      <PreprocessingButtons
        current={hits.preprocessing}
        onChange={onPreprocessingChange}
      />

      {/* 비교 화면과 기본값이 다르다 — 감출 수 없다 */}
      {hits.preprocessing === "background_excluded" && (
        <p className="mb-2 text-[11px] text-gray-500">
          순위는 배경가스를 제외하고 냈습니다.{" "}
          <strong>비교 화면의 기본은 원자료</strong>이므로 같은 물질이라도
          점수가 다를 수 있습니다.
        </p>
      )}

      {/* AI 후보가 참조 DB 에 없을 때 — 그래도 순위는 보여준다 */}
      {!hits.ai_compound_in_library && hits.ai_compound_name && (
        <div className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          AI 후보 <strong>{hits.ai_compound_name}</strong> 은(는) 참조 DB에
          없습니다. 아래는 보유한 참조물질과의 유사도 순위입니다.
        </div>
      )}

      <div className="max-h-[26rem] flex-1 overflow-y-auto rounded border border-gray-200">
        <table className="w-full text-xs tabular-nums">
          <caption className="sr-only">
            참조 스펙트럼 순위 — 실험 스펙트럼과 참조물질의 유사도 순위
          </caption>
          <thead className="sticky top-0 bg-gray-50 text-gray-600">
            <tr>
              <th scope="col" className="px-2 py-1 text-right font-medium">
                #
              </th>
              <th scope="col" className="px-2 py-1 text-right font-medium">
                유사도
              </th>
              <th scope="col" className="px-2 py-1 text-left font-medium">
                물질명
              </th>
            </tr>
          </thead>
          <tbody>
            {hits.hits.map((hit) => {
              const isSelected = hit.compound_id === selectedCompoundId;
              const isTied = tiedScores.has(hit.score.toFixed(TIE_DIGITS));
              return (
                <tr
                  key={hit.compound_id}
                  aria-selected={isSelected}
                  onClick={() => onSelect?.(hit.compound_id)}
                  className={`border-t border-gray-100 ${
                    onSelect ? "cursor-pointer hover:bg-gray-50" : ""
                  } ${isSelected ? "bg-indigo-50" : ""}`}
                >
                  <td className="px-2 py-1 text-right text-gray-500">
                    {hit.rank}
                  </td>
                  <td className="px-2 py-1 text-right font-medium text-gray-900">
                    {hit.score.toFixed(3)}
                    {isTied && (
                      <span
                        className="ml-1 text-[10px] font-normal text-amber-700"
                        title="같은 점수인 후보가 더 있습니다 — 이 조건에서는 서로 구별되지 않습니다."
                      >
                        동점
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-gray-800">
                    {hit.compound_name}
                    {hit.is_ai_identified && (
                      <span className="ml-1.5 rounded bg-indigo-100 px-1 py-0.5 text-[10px] text-indigo-700">
                        AI 후보
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-1 text-[11px] text-gray-500">
        점수 순으로만 정렬했습니다. 어디까지를 후보로 볼지는 표시하지 않습니다.
        <br />
        AI 결과와 이 순위는{" "}
        <strong>
          참조 라이브러리와 점수 정의가 달라 직접 비교할 수 없습니다.
        </strong>{" "}
        어느 쪽도 단독으로 물질을 동정하지 않습니다.
      </p>
    </Frame>
  );
};

export default memo(SpectrumHitList);
